// src/components/PatientActivityTrail.jsx
//
// The patient's transaction trail, for a navigator.
//
// patient_audit_log has been written to since V79 — uploads, triage runs,
// payments, lab orders, appointments, critical escalations, and now the
// intervention date — and none of it was visible anywhere in the product that
// recorded it. The table's stated purpose is "what did this patient do, or have
// done to them, and why", which is a question a navigator asks about the patient
// in front of them, not one they open a SQL client for.
//
// Read-only by design. The trail is append-only by convention in the backend
// (application code never updates or deletes a row), so there is nothing here to
// edit and no action to offer.

import { useCallback, useEffect, useState } from 'react';
import { patientAPI } from '../services/api';
import EmptyState from './EmptyState';

// Every event type the backend can write (V79 + V90 + V91 + V111 + V132 +
// V146 + V151 + V171). An unknown type still renders — with its name turned
// into a sentence-case label and a neutral icon — rather than vanishing,
// because a new backend event type reaching an older frontend should degrade
// to "something happened and here is what it was called", not to a blank row.
const EVENT_META = {
  PATIENT_REGISTERED:              { icon: '🌱', label: 'Registered' },
  REPORT_UPLOADED:                 { icon: '📄', label: 'Report uploaded' },
  REPORT_DELETED:                  { icon: '🗑️', label: 'Report deleted' },
  TRIAGE_EXECUTED:                 { icon: '🧠', label: 'Triage run' },
  INTERVENTION_DATE_SET:           { icon: '🎯', label: 'Intervention date set' },
  CRITICAL_ESCALATION_RAISED:      { icon: '🚨', label: 'Critical escalation raised' },
  CRITICAL_ESCALATION_ACKNOWLEDGED:{ icon: '✅', label: 'Escalation acknowledged' },
  CRITICAL_ESCALATION_RESOLVED:    { icon: '🏁', label: 'Escalation resolved' },
  CRITICAL_ESCALATION_ASSIGNED:    { icon: '👤', label: 'Escalation assigned to navigator' },
  CRITICAL_ESCALATION_SPECIALIST_SUGGESTED: { icon: '🩺', label: 'Specialist suggested' },
  CRITICAL_ESCALATION_SPECIALIST_BOOKED:    { icon: '📅', label: 'Specialist appointment booked' },
  CRITICAL_ESCALATION_SNOOZED:     { icon: '⏸️', label: 'Escalation snoozed' },
  LAB_TEST_ORDERED:                { icon: '🧪', label: 'Lab test ordered' },
  APPOINTMENT_BOOKED:              { icon: '📅', label: 'Appointment booked' },
  APPOINTMENT_CANCELLED:           { icon: '❌', label: 'Appointment cancelled' },
  PAYMENT_MADE:                    { icon: '💳', label: 'Payment' },
  CHAT_CONTACT_INITIATED:          { icon: '💬', label: 'Contacted staff' },
  NOTE_ADDED:                      { icon: '📝', label: 'Note added' },
  ACTION_ITEM_ADDED:               { icon: '📌', label: 'Action item added' },
  ACTION_ITEM_COMPLETED:           { icon: '✔️', label: 'Action item completed' },
  NAVIGATOR_REASSIGNED:            { icon: '🔁', label: 'Navigator reassigned' },
  AADHAR_NUMBER_REVEALED:          { icon: '👁️', label: 'Aadhaar number viewed' },
  WHATSAPP_NOTIFICATION_PREFERENCE_CHANGED: { icon: '📱', label: 'WhatsApp notifications changed' },
  APP_NOTIFICATION_PREFERENCE_CHANGED:      { icon: '🔔', label: 'In-app notifications changed' },
};

// SOME_NEW_EVENT -> "Some new event", so an unmapped type still reads as words.
function fallbackMeta(eventType) {
  const words = (eventType || '').toLowerCase().replace(/_+/g, ' ').trim();
  return {
    icon: '📋',
    label: words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Event',
  };
}

// The two events a navigator should not have to hunt for in a list.
const URGENT = new Set(['CRITICAL_ESCALATION_RAISED']);

const ACTOR_LABEL = {
  PATIENT:    'the patient',
  NAVIGATOR:  'a navigator',
  DOCTOR:     'a doctor',
  SUBSCRIBER: 'the subscriber',
  // actor_id is null for SYSTEM by design — a scheduled job or automated rule
  // has no human to attribute it to.
  SYSTEM:     'the system',
};

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function PatientActivityTrail({ patientId }) {
  const [entries, setEntries] = useState([]);
  const [page, setPage]       = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async (pageToLoad) => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await patientAPI.auditLog(patientId, { page: pageToLoad, size: 20 });
      // [TEST] comments are QA scratch data (lifecycle/toggle-stress probes)
      // — the marker isn't always at the start (e.g. "Action item marked
      // complete: [TEST] Rapid toggle stress"), so check the whole comment,
      // not just its prefix. Never worth showing a navigator.
      const fetched = (res.data?.entries || []).filter(e => !e.comment?.includes('[TEST]'));
      // Append rather than replace: "Load more" walks further back in time, so
      // the earlier pages must stay on screen.
      setEntries(prev => (pageToLoad === 0 ? fetched : [...prev, ...fetched]));
      setTotalPages(res.data?.totalPages ?? 0);
      setError('');
    } catch {
      setError('Could not load the activity trail.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { setPage(0); load(0); }, [load]);

  const hasMore = page + 1 < totalPages;

  return (
    <div className="card">
      <div className="card-title">🕓 Activity Trail</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
        Everything recorded for this patient, newest first — uploads, triage runs,
        payments, lab orders, appointments, escalations and care decisions.
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && entries.length === 0 && !error && (
        <EmptyState
          icon="clock"
          title="Nothing recorded yet"
          message="Activity appears here as reports are uploaded, appointments booked and care decisions made."
        />
      )}

      {entries.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {entries.map(e => {
            const meta = EVENT_META[e.eventType] || fallbackMeta(e.eventType);
            const urgent = URGENT.has(e.eventType);
            return (
              <div key={e.id} style={{
                display: 'flex', gap: 12, padding: '12px 14px',
                borderRadius: 'var(--radius-sm)',
                background: urgent ? 'var(--red-bg)' : 'var(--bg-subtle)',
                border: `1px solid ${urgent ? 'var(--red)' : 'var(--bg-hover)'}`,
              }}>
                <div style={{ fontSize: 18, lineHeight: 1.3 }} aria-hidden="true">{meta.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', minWidth: 0, overflowWrap: 'anywhere' }}>
                      {meta.label}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {formatWhen(e.createdAt)}
                    </span>
                  </div>
                  {e.comment && (
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.55, overflowWrap: 'anywhere' }}>
                      {e.comment}
                    </div>
                  )}
                  {/* Role, not a name: several writers record an actorType with a
                      null actorId, so resolving names would label some rows and
                      leave others blank. A consistent role reads better than a
                      partial name. */}
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    by {ACTOR_LABEL[e.actorType] || 'unknown'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: entries.length ? 10 : 0 }}>
          {[0, 1, 2].map(i => <div key={i} className="skeleton" style={{ height: 62, borderRadius: 'var(--radius-sm)' }} />)}
        </div>
      )}

      {hasMore && !loading && (
        <button type="button" className="btn btn-ghost" style={{ marginTop: 14 }}
          onClick={() => { const next = page + 1; setPage(next); load(next); }}>
          Load older activity
        </button>
      )}
    </div>
  );
}
